import {Inject, Injectable, OnDestroy} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {
    WORKBENCH_IPC_SCHEMA_VERSION,
    WorkbenchClaimPayload,
    WorkbenchFocusPayload,
    WorkbenchIpcMessage,
    WorkbenchIpcMessageType,
    WorkbenchIpcPayloadMap,
    WorkbenchIpcSenderRole,
    WorkbenchIpcSendOptions,
    WorkbenchMode,
    WorkbenchModeStatePayload,
    WorkbenchOpenPayload,
    WorkbenchPopoutSyncPayload,
    WorkbenchPresenceLocation,
    WorkbenchPresencePayload,
    WorkbenchQueryPayload,
    WorkbenchSetModePayload,
} from './workbench-ipc.types';
import {
    WORKBENCH_IPC_ADAPTER_FACTORY,
    WORKBENCH_IPC_CHANNEL_NAME,
    WorkbenchIpcAdapter,
    WorkbenchIpcAdapterFactory
} from './workbench-ipc-adapter';
import {ProjectId, UserId} from '@webmate-web-core/shared/types/ids';

@Injectable({
    providedIn: 'root'
})
export class WorkbenchIpcService implements OnDestroy {
    readonly senderId = this.generateId('workbench-tab');
    readonly createdAt = Date.now();

    private readonly adapter: WorkbenchIpcAdapter;
    private readonly messagesSubject = new Subject<WorkbenchIpcMessage>();
    private readonly subscriptions = new Subscription();
    private currentUserId?: UserId;

    constructor(@Inject(WORKBENCH_IPC_CHANNEL_NAME) channelName: string,
                @Inject(WORKBENCH_IPC_ADAPTER_FACTORY) adapterFactory: WorkbenchIpcAdapterFactory,
                private readonly userService: UserService) {
        this.adapter = adapterFactory(channelName);
        this.subscriptions.add(this.userService.getCurrentUserData().subscribe(user => this.currentUserId = user.id));
        this.subscriptions.add(this.adapter.messages$
        .pipe(filter(message => this.acceptMessageForCurrentUser(message)))
        .subscribe(message => this.messagesSubject.next(message)));
    }

    send<T extends WorkbenchIpcMessageType>(
        type: T,
        payload: WorkbenchIpcPayloadMap[T],
        options: WorkbenchIpcSendOptions = {},
        senderRole: WorkbenchIpcSenderRole = 'workbench'
    ): WorkbenchIpcMessage<T> {
        const message: WorkbenchIpcMessage<T> = {
            schemaVersion: WORKBENCH_IPC_SCHEMA_VERSION,
            messageId: this.generateId('workbench-ipc'),
            senderId: this.senderId,
            senderRole,
            userId: this.currentUserId,
            timestamp: Date.now(),
            projectId: options.projectId,
            correlationId: options.correlationId,
            type,
            payload
        };
        this.adapter.send(message as WorkbenchIpcMessage);
        return message;
    }

    observe(): Observable<WorkbenchIpcMessage> {
        return this.messagesSubject.asObservable();
    }

    observeType<T extends WorkbenchIpcMessageType>(type: T): Observable<WorkbenchIpcMessage<T>> {
        return this.observe().pipe(filter((message): message is WorkbenchIpcMessage<T> => message.type === type));
    }

    observeCorrelation(correlationId: string): Observable<WorkbenchIpcMessage> {
        return this.observe().pipe(filter(message => message.correlationId === correlationId));
    }

    query(projectId: ProjectId, correlationId = this.generateId('workbench-correlation'),
          senderRole: WorkbenchIpcSenderRole = 'webmate'): WorkbenchIpcMessage<'workbench:query'> {
        const payload: WorkbenchQueryPayload = {requestedAt: Date.now()};
        return this.send('workbench:query', payload, {projectId, correlationId}, senderRole);
    }

    claim(projectId: ProjectId, activeMode: WorkbenchMode, ready = true, correlationId?: string,
          presence: WorkbenchPresenceLocation = {}): WorkbenchIpcMessage<'workbench:claim'> {
        const payload: WorkbenchClaimPayload = {
            tabId: this.senderId,
            createdAt: this.createdAt,
            ready,
            activeMode,
            currentUrl: presence.currentUrl,
            windowName: presence.windowName
        };
        return this.send('workbench:claim', payload, {projectId, correlationId}, 'workbench');
    }

    open(projectId: ProjectId, payload: WorkbenchOpenPayload = {}, correlationId?: string,
         senderRole: WorkbenchIpcSenderRole = 'webmate'): WorkbenchIpcMessage<'workbench:open'> {
        return this.send('workbench:open', payload, {projectId, correlationId}, senderRole);
    }

    focus(projectId: ProjectId, payload: WorkbenchFocusPayload = {}, correlationId?: string,
          senderRole: WorkbenchIpcSenderRole = 'webmate'): WorkbenchIpcMessage<'workbench:focus'> {
        return this.send('workbench:focus', payload, {projectId, correlationId}, senderRole);
    }

    setMode(projectId: ProjectId, payload: WorkbenchSetModePayload, correlationId?: string,
            senderRole: WorkbenchIpcSenderRole = 'webmate'): WorkbenchIpcMessage<'workbench:set-mode'> {
        return this.send('workbench:set-mode', payload, {projectId, correlationId}, senderRole);
    }

    modeState(projectId: ProjectId, payload: WorkbenchModeStatePayload, correlationId?: string,
              senderRole: WorkbenchIpcSenderRole = 'workbench'): WorkbenchIpcMessage<'workbench:mode-state'> {
        return this.send('workbench:mode-state', payload, {projectId, correlationId}, senderRole);
    }

    announce(projectId: ProjectId, activeMode: WorkbenchMode, ready = true,
             presence: WorkbenchPresenceLocation = {}): WorkbenchIpcMessage<'workbench:announce'> {
        return this.send('workbench:announce', this.presencePayload(activeMode, ready, presence), {projectId}, 'workbench');
    }

    heartbeat(projectId: ProjectId, activeMode: WorkbenchMode, ready = true,
              presence: WorkbenchPresenceLocation = {}): WorkbenchIpcMessage<'workbench:heartbeat'> {
        return this.send('workbench:heartbeat', this.presencePayload(activeMode, ready, presence), {projectId}, 'workbench');
    }

    popoutSync(payload: WorkbenchPopoutSyncPayload, projectId?: ProjectId, correlationId?: string,
               senderRole: WorkbenchIpcSenderRole = 'workbench'): WorkbenchIpcMessage<'workbench:popout-sync'> {
        return this.send('workbench:popout-sync', payload, {projectId, correlationId}, senderRole);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.adapter.close();
        this.messagesSubject.complete();
    }

    private presencePayload(activeMode: WorkbenchMode, ready: boolean, presence: WorkbenchPresenceLocation): WorkbenchPresencePayload {
        return {
            tabId: this.senderId,
            createdAt: this.createdAt,
            ready,
            activeMode,
            currentUrl: presence.currentUrl,
            windowName: presence.windowName
        };
    }

    private acceptMessageForCurrentUser(message: WorkbenchIpcMessage): boolean {
        return !message.userId || !this.currentUserId || message.userId === this.currentUserId;
    }

    private generateId(prefix: string): string {
        const cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined;
        if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
            return `${prefix}-${cryptoRef.randomUUID()}`;
        }
        return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}
